import { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Shuffle, Heart, Sparkles, Share2, Facebook, Twitter, MessageCircle, Link as LinkIcon } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { fetchMedia, FILE_URL, API } from "@/lib/api";

export const KindnessReel = () => {
  const [videos, setVideos] = useState([]);
  const [current, setCurrent] = useState(null);

  const pickRandom = useCallback(
    (list, exceptId) => {
      const pool = list.filter((v) => v.id !== exceptId);
      const source = pool.length ? pool : list;
      if (!source.length) return null;
      return source[Math.floor(Math.random() * source.length)];
    },
    []
  );

  useEffect(() => {
    (async () => {
      try {
        const kindness = await fetchMedia("kindness");
        let vids = kindness.filter((m) => m.media_type === "video");
        if (vids.length === 0) {
          const gallery = await fetchMedia("gallery");
          vids = gallery.filter((m) => m.media_type === "video");
        }
        setVideos(vids);
        setCurrent(pickRandom(vids));
      } catch {
        /* ignore */
      }
    })();
  }, [pickRandom]);

  const shuffle = () => setCurrent((c) => pickRandom(videos, c?.id));

  const [shareOpen, setShareOpen] = useState(false);
  const [shares, setShares] = useState(null);

  useEffect(() => {
    axios.get(`${API}/kindness/stats`).then(({ data }) => setShares(data.shares)).catch(() => {});
  }, []);

  const recordShare = () => {
    setShares((n) => (typeof n === "number" ? n + 1 : n));
    axios.post(`${API}/kindness/share`).then(({ data }) => setShares(data.shares)).catch(() => {});
  };

  const shareUrl = typeof window !== "undefined" ? `${window.location.origin}/#kindness` : "";
  const shareText = current?.caption
    ? `${current.caption} — a random act of kindness from Mask Giver. Saving one soul at a time.`
    : "Watch a random act of kindness from Mask Giver. Saving one soul at a time.";

  const nativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: "Mask Giver", text: shareText, url: shareUrl });
        recordShare();
        return;
      } catch {
        return;
      }
    }
    setShareOpen((o) => !o);
  };

  const shareTo = (platform) => {
    const u = encodeURIComponent(shareUrl);
    const t = encodeURIComponent(shareText);
    const links = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${u}`,
      twitter: `https://twitter.com/intent/tweet?text=${t}&url=${u}`,
      whatsapp: `https://wa.me/?text=${t}%20${u}`,
    };
    window.open(links[platform], "_blank", "noopener,noreferrer");
    recordShare();
    setShareOpen(false);
  };

  const copyLink = () => {
    navigator.clipboard.writeText(shareUrl);
    toast.success("Link copied — share the kindness!");
    recordShare();
    setShareOpen(false);
  };

  return (
    <section id="kindness" data-testid="kindness-reel" className="relative bg-[#0a0a0a] py-24 md:py-36 border-y border-[#1a1a1a] overflow-hidden">
      <div className="mx-auto max-w-[1400px] px-5 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <motion.div
          className="lg:col-span-4"
          initial={{ opacity: 0, x: -30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
        >
          <div className="flex items-center gap-2 mb-5 text-green">
            <Sparkles size={16} />
            <span className="label !text-green">Random Act</span>
          </div>
          <h2 className="font-display uppercase text-white leading-[0.9] text-4xl sm:text-6xl">
            A random act of <span className="text-green glow-green">kindness</span>
          </h2>
          <p className="font-sub text-lg text-zinc-400 mt-6 font-light max-w-md">
            Hit shuffle and watch a random moment of love from the streets. Every clip is a
            reminder that one small act can change a life.
          </p>
          {typeof shares === "number" && shares > 0 && (
            <p data-testid="kindness-share-count" className="font-sub text-sm text-zinc-500 mt-5 flex items-center gap-2">
              <Heart size={15} className="text-green" />
              <span className="text-green font-semibold">{shares.toLocaleString()}</span> acts of kindness shared
            </p>
          )}
          {videos.length > 0 && (
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <button
                data-testid="kindness-shuffle"
                onClick={shuffle}
                className="btn-fill border border-green text-green font-sub font-semibold px-8 py-3.5 rounded-full flex items-center gap-3 box-glow"
              >
                <Shuffle size={18} /> Shuffle kindness
              </button>

              <div className="relative">
                <button
                  data-testid="kindness-share"
                  onClick={nativeShare}
                  className="btn-fill border border-[#1a1a1a] hover:border-green text-white font-sub font-semibold px-6 py-3.5 rounded-full flex items-center gap-2"
                >
                  <Heart size={16} className="text-green" /> Share this act
                </button>

                <AnimatePresence>
                  {shareOpen && (
                    <motion.div
                      data-testid="kindness-share-menu"
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 8 }}
                      transition={{ duration: 0.2 }}
                      className="absolute left-0 mt-2 z-20 bg-[#050505] border border-[#1a1a1a] rounded-2xl p-2 w-52 shadow-xl"
                    >
                      {[
                        { id: "facebook", label: "Facebook", Icon: Facebook, fn: () => shareTo("facebook") },
                        { id: "twitter", label: "X / Twitter", Icon: Twitter, fn: () => shareTo("twitter") },
                        { id: "whatsapp", label: "WhatsApp", Icon: MessageCircle, fn: () => shareTo("whatsapp") },
                        { id: "copy", label: "Copy link", Icon: LinkIcon, fn: copyLink },
                      ].map(({ id, label, Icon, fn }) => (
                        <button
                          key={id}
                          data-testid={`kindness-share-${id}`}
                          onClick={fn}
                          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-zinc-300 hover:bg-[#111] hover:text-green transition-colors font-sub text-sm"
                        >
                          <Icon size={16} /> {label}
                        </button>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          )}
        </motion.div>

        <motion.div
          className="lg:col-span-8"
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
        >
          <div className="relative w-full aspect-video bg-black border border-[#1a1a1a] overflow-hidden">
            {current ? (
              <video
                key={current.id}
                data-testid="kindness-video"
                src={FILE_URL(current.url)}
                className="w-full h-full object-cover"
                autoPlay
                muted
                loop
                playsInline
                controls
              />
            ) : (
              <div data-testid="kindness-empty" className="w-full h-full flex flex-col items-center justify-center text-center px-6">
                <Heart className="text-green mb-4" size={40} />
                <p className="font-sub text-white text-lg">No kindness clips yet</p>
                <p className="font-sub text-zinc-500 mt-1 max-w-sm">
                  Upload short videos under the <span className="text-green">Kindness</span> category in the{" "}
                  <a href="/admin" className="text-green link-underline">admin studio</a> — they'll shuffle and play right here.
                </p>
              </div>
            )}
            {current?.caption && (
              <span className="absolute bottom-4 left-4 right-4 font-sub text-sm text-white/90 bg-black/50 backdrop-blur px-4 py-2 rounded-full w-fit">
                {current.caption}
              </span>
            )}
          </div>
        </motion.div>
      </div>
    </section>
  );
};
